import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink, MessageSquarePlus } from 'lucide-react';

/**
 * Customer Reviews + Expert Endorsements block (Product page).
 *
 * Strategy (Feb 2026): Real first-party Google customer reviews drive
 * the visible UI AND the AggregateRating JSON-LD (Google's #1 rule:
 * visible content must match the schema 1:1). Real expert endorsements
 * (Dr Antony, Medilink, BBC, Pressat) are rendered as a smaller
 * "Recognised by" strip below — they're 100% real public statements
 * but are NOT included in AggregateRating, which Google reserves for
 * customer reviews.
 *
 * TODO: when Trustpilot Business Unit is wired, ALSO swap in their
 * widget. Until then, Google Business Profile is the canonical source.
 */

// Real Google Business Profile reviews (5.0★, 6 reviews as of 2026-08).
// MHRA/ASA compliance: quotes are lightly trimmed to remove references
// to treating/relieving specific medical conditions (e.g. arthritis,
// joint-pain relief). Full unedited reviews remain on Google via the
// "View all reviews on Google" link below. When new reviews arrive,
// edit this array and CURATED_TOTAL_REVIEWS.
const GOOGLE_REVIEWS = [
  {
    id: 'elaine',
    author: 'Elaine',
    rating: 5,
    datePublished: '2026-05-20',
    relativeDate: 'Google review',
    quote: 'Great service, great staff.',
    source: 'Google',
    ownerReply: 'Thank you for your kind words, Elaine!',
  },
  {
    id: 'farida-lal',
    author: 'Farida Lal',
    rating: 5,
    datePublished: '2026-05-23',
    relativeDate: 'Google review',
    quote:
      'What I like most is that the supplement feels natural. The capsules are easy to swallow, with no unpleasant taste or discomfort. The packaging is secure, the instructions are clear, and I appreciate the good-quality turmeric extract.',
    source: 'Google',
  },
  {
    id: 'areeba-wahdi',
    author: 'Areeba Wahdi',
    rating: 5,
    datePublished: '2026-05-18',
    relativeDate: 'Google review',
    quote: 'Highly recommend it — very happy with the product and the service.',
    source: 'Google',
  },
];

// Total public Google reviews on the Business Profile (5.0 average).
// Kept as a constant so the visible count + AggregateRating stay honest
// even though we display a compliant subset of quotes above.
const CURATED_TOTAL_REVIEWS = 6;

const EXPERT_ENDORSEMENTS = [
  {
    id: 'benny-antony',
    author: 'Dr Benny Antony, PhD',
    role: 'Scientific Advisor · Co-developer, BCM-95®',
  },
  {
    id: 'medilink-midlands',
    author: 'Medilink Midlands',
    role: 'UK Life Sciences Industry Association',
  },
  {
    id: 'bbc-humberside',
    author: 'BBC Radio Humberside',
    role: 'On-air feature · 7m 45s segment',
  },
  {
    id: 'pressat-curcumin',
    author: 'Pressat (UK Press Release Distribution)',
    role: 'Editorial · Joint Health Category',
  },
];

const PRODUCT_ID = '94056720-f372-4eef-bcfe-74063137a6e5';
const PRODUCT_URL = `https://iiypv.com/products/${PRODUCT_ID}`;
const GOOGLE_PROFILE_URL = 'https://www.google.com/maps?cid=1610033611455267559';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const computeAggregate = (reviews) =>
  reviews.length === 0 ? '0.0' : (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);

const Stars = ({ value, size = 14 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
      />
    ))}
  </div>
);

// Coloured letter avatar fallback (Google-style)
const Avatar = ({ name }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const palette = ['bg-emerald-700', 'bg-amber-600', 'bg-rose-600', 'bg-sky-700', 'bg-indigo-700'];
  const color = palette[(name?.charCodeAt(0) || 0) % palette.length];
  return (
    <div className={`shrink-0 w-10 h-10 rounded-full ${color} text-white flex items-center justify-center font-bold text-base`}>
      {initial}
    </div>
  );
};

/**
 * Trustpilot Trustbox widget slot. Renders only when the
 * REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID env var is set. To enable:
 *   1. Sign up at https://business.trustpilot.com
 *   2. Find your Business Unit ID (Settings → Integrations → Trustbox)
 *   3. Add it to frontend/.env: REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID=xxxx
 *   4. Restart frontend supervisor
 * The Trustpilot script auto-renders the widget; no further code change.
 */
const TRUSTPILOT_BUSINESS_UNIT_ID = process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID;

const TrustpilotWidget = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (!TRUSTPILOT_BUSINESS_UNIT_ID) return;
    // Load the Trustpilot bootstrap script once
    const existing = document.querySelector('script[src*="tp.widget.bootstrap.min.js"]');
    const onReady = () => {
      if (window.Trustpilot && ref.current) {
        window.Trustpilot.loadFromElement(ref.current, true);
      }
    };
    if (existing) { onReady(); return; }
    const s = document.createElement('script');
    s.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    s.async = true;
    s.onload = onReady;
    document.head.appendChild(s);
  }, []);

  if (!TRUSTPILOT_BUSINESS_UNIT_ID) return null;
  return (
    <div className="mt-8 pt-6 border-t border-emerald-700/10" data-testid="trustpilot-widget">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">
        Verified on Trustpilot
      </div>
      <div
        ref={ref}
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id="56278e9abfbbba0bdcd568bc"
        data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
        data-style-height="52px"
        data-style-width="100%"
        data-theme="light"
      >
        <a
          href={`https://uk.trustpilot.com/review/iiypv.com`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-700 underline text-sm"
        >
          Trustpilot reviews
        </a>
      </div>
    </div>
  );
};

export default function ReviewsAndRatings({ withSchema = true }) {
  // Try live Google Places API (env-driven on backend). Falls back to
  // the hardcoded GOOGLE_REVIEWS array when the endpoint returns no
  // reviews (i.e. GOOGLE_PLACES_* env vars not set on the backend).
  const [reviews, setReviews] = useState(GOOGLE_REVIEWS);
  const [externalCount, setExternalCount] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE}/api/reviews/google`)
      .then((r) => {
        if (!active) return;
        const live = r.data?.reviews || [];
        if (live.length > 0) {
          setReviews(live);
        }
        if (r.data?.userRatingCount) {
          setExternalCount(r.data.userRatingCount);
        }
      })
      .catch(() => { /* keep hardcoded fallback */ });
    return () => { active = false; };
  }, []);

  const aggregateRatingValue = computeAggregate(reviews);

  const reviewSchema = reviews.map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    datePublished: r.datePublished,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: r.quote,
    publisher: { '@type': 'Organization', name: 'Google' },
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${PRODUCT_URL}#product`,
    name: 'BCM-95® Curcumin Food Supplement',
    url: PRODUCT_URL,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: aggregateRatingValue,
      reviewCount: externalCount || CURATED_TOTAL_REVIEWS,
      bestRating: 5,
      worstRating: 1,
    },
    review: reviewSchema,
  };

  return (
    <section className="my-16" data-testid="customer-reviews">
      {withSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      {/* ============ Header row: aggregate + Google source ============ */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-2">
            What customers say
          </div>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-emerald-900 leading-tight">
            Trusted by People Who Value Staying Active
          </h2>
        </div>
        <div className="flex items-center gap-3" data-testid="aggregate-rating">
          <Stars value={Math.round(parseFloat(aggregateRatingValue))} size={20} />
          <div className="text-sm leading-tight">
            <strong className="text-emerald-900 text-base">{aggregateRatingValue} / 5</strong>
            <div className="text-slate-500 text-xs mt-0.5">
              {externalCount ? `${externalCount} Google reviews` : `${CURATED_TOTAL_REVIEWS} Google reviews`}
            </div>
          </div>
        </div>
      </div>

      {/* ============ Reviews ============ */}
      <div className="grid sm:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <article
            key={r.id}
            className="rounded-2xl border border-emerald-700/15 bg-white p-5 hover:border-emerald-700/35 transition-colors flex flex-col"
            data-testid={`review-${r.id}`}
          >
            <div className="flex items-start gap-3 mb-3">
              <Avatar name={r.author} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-bold text-emerald-900 text-sm truncate">{r.author}</div>
                  <div className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-emerald-700/70 flex items-center gap-1">
                    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.47 12c0-.73.13-1.44.36-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
                    </svg>
                    Google
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Stars value={r.rating} />
                  <span className="text-xs text-slate-500">· {r.relativeDate}</span>
                </div>
              </div>
            </div>
            <p className="text-[15px] leading-relaxed text-slate-700 flex-1">
              &ldquo;{r.quote}&rdquo;
            </p>
            {r.ownerReply && (
              <div className="mt-4 pl-3 border-l-2 border-emerald-700/30 text-xs text-slate-600 italic">
                <span className="font-bold text-emerald-800 not-italic">Response from the owner · </span>
                {r.ownerReply}
              </div>
            )}
          </article>
        ))}
      </div>

      {/* ============ CTAs row: view all / leave a review ============ */}
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-700/25 bg-white px-5 py-2.5 text-sm font-bold text-emerald-900 hover:bg-emerald-50 hover:border-emerald-700/45 transition-colors"
          data-testid="view-google-reviews"
        >
          View all reviews on Google <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 text-white px-5 py-2.5 text-sm font-bold hover:bg-emerald-800 transition-colors"
          data-testid="leave-google-review"
        >
          <MessageSquarePlus className="w-3.5 h-3.5" /> Leave a Google review
        </a>
      </div>

      {/* ============ Trustpilot Trustbox (env-driven) ============ */}
      <TrustpilotWidget />

      {/* ============ Recognised by — smaller credibility strip ============ */}
      <div className="mt-10 pt-8 border-t border-emerald-700/10">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-4">
          Also recognised by
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {EXPERT_ENDORSEMENTS.map((e) => (
            <div
              key={e.id}
              className="rounded-xl bg-emerald-50/40 border border-emerald-700/10 px-3 py-3"
              data-testid={`endorsement-${e.id}`}
            >
              <div className="font-bold text-emerald-900 text-xs leading-snug">{e.author}</div>
              <div className="text-[11px] text-slate-500 leading-snug mt-1">{e.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
